import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, ComposedChart, Bar, Line, Legend } from 'recharts';
import { Leaf, ArrowUpRight, ArrowDownRight } from 'lucide-react';

// Hourly facility load (kW) — 16-rack small DC, IT base ~88 kW + cooling overhead
const consumptionData = [
    { time: '00:00', electrical: 78, cooling: 22 },
    { time: '04:00', electrical: 76, cooling: 21 },
    { time: '08:00', electrical: 92, cooling: 28 },
    { time: '12:00', electrical: 118, cooling: 38 },
    { time: '16:00', electrical: 124, cooling: 42 },
    { time: '20:00', electrical: 104, cooling: 32 },
    { time: '24:00', electrical: 84, cooling: 24 },
];

// On-site solar + small storage + utility PPA with renewable attribution
const sourceData = [
    { name: 'Solar (On-site)', value: 22, color: '#60a5fa' },
    { name: 'Wind PPA', value: 31, color: '#4ade80' },
    { name: 'Grid (Renewable)', value: 36, color: '#a78bfa' },
    { name: 'Grid (Fossil)', value: 11, color: '#fb923c' },
];

// Daily water makeup for cooling tower + chilled water loop (gallons) and chiller plant efficiency
const efficiencyData = [
    { day: 'MON', water: 620, efficiency: 79 },
    { day: 'TUE', water: 595, efficiency: 81 },
    { day: 'WED', water: 680, efficiency: 77 },
    { day: 'THU', water: 655, efficiency: 80 },
    { day: 'FRI', water: 710, efficiency: 84 },
    { day: 'SAT', water: 510, efficiency: 87 },
    { day: 'SUN', water: 485, efficiency: 89 },
];

const StatCard = ({ title, value, subValue, change, trend, trendColor }: any) => {
    const getFullBarColor = (color: string) => {
        switch (color) {
            case 'red': return 'bg-red-500/20 text-red-500';
            case 'green': return 'bg-green-500/20 text-green-500';
            case 'orange': return 'bg-orange-500/20 text-orange-500';
            default: return 'bg-zinc-800/50 text-zinc-400';
        }
    };

    return (
        <div className="bg-[#1a1d24] border border-[#2e323a] rounded-lg p-5 flex flex-col justify-between shadow-sm min-h-[120px]">
            <div className="text-zinc-500 text-[11px] font-bold tracking-wider mb-2 uppercase">{title}</div>
            <div className="mb-4">
                <div className="text-3xl font-bold text-white leading-none">{value}</div>
                <div className="text-xs text-zinc-500 mt-2">{subValue}</div>
            </div>
            <div className={`px-2 py-1.5 rounded text-[11px] font-bold w-full flex justify-between items-center mt-2 ${getFullBarColor(trendColor)}`}>
                <span className="flex items-center gap-1">
                    {trend === 'up' ? <ArrowUpRight size={12} /> : trend === 'down' ? <ArrowDownRight size={12} /> : null}
                    {change}
                </span>
            </div>
        </div>
    );
};

const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
        return (
            <div className="bg-[#1a1d24] border border-[#2e323a] p-3 rounded-lg shadow-xl">
                <p className="text-zinc-400 text-xs mb-2">{label}</p>
                {payload.map((entry: any, index: number) => (
                    <div key={index} className="flex items-center gap-2 text-sm font-semibold" style={{ color: entry.color }}>
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color }} />
                        {entry.name}: {entry.value}
                    </div>
                ))}
            </div>
        );
    }
    return null;
};

const EnergyOverview = () => {
    return (
        <div className="w-full h-full flex flex-col gap-4 text-white pb-10">
            {/* Top 6 Cards */}
            <div className="grid grid-cols-2 md:grid-cols-6 gap-5">
                <StatCard title="TOTAL ENERGY" value="2,994" subValue="kWh Today" change="3.2% vs Yesterday" trend="down" trendColor="green" />
                <StatCard title="WATER USAGE" value="620" subValue="Gallons Today" change="1.8% vs Yesterday" trend="down" trendColor="green" />
                <StatCard title="BTU COOLING" value="307K" subValue="BTU/hr Current" change="25.0 tons load" trend="none" trendColor="orange" />
                <StatCard title="SCOPE 2 CO2" value="0.29" subValue="Tons Today" change="11% vs Target" trend="down" trendColor="green" />
                <StatCard title="ACTIVE DEVICES" value="478" subValue="of 500 Total" change="99.1% Uptime" trend="up" trendColor="green" />
                <StatCard title="ENERGY COST" value="$10,140" subValue="Projected Month" change="$240 vs Budget" trend="up" trendColor="orange" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 flex-1">

                {/* Left Column (Carbon Credits & Infra) */}
                <div className="flex flex-col gap-4 col-span-1">
                    {/* Carbon Credits */}
                    <div className="bg-[#1a1d24] border border-[#2e323a] rounded-lg p-5 relative overflow-hidden flex flex-col">
                        <div className="flex items-center gap-2 mb-4">
                            <div className="bg-green-500/20 p-1.5 rounded">
                                <Leaf size={16} className="text-green-500" />
                            </div>
                            <h3 className="font-bold text-xs">Carbon Credits Accumulated</h3>
                        </div>

                        <div className="flex items-baseline gap-2 mb-1">
                            <span className="text-3xl font-bold text-green-500">82</span>
                            <span className="text-xs text-zinc-400">tCO₂</span>
                            <span className="text-[10px] bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full ml-auto">+9% this month</span>
                        </div>

                        <div className="text-[10px] text-zinc-400 mb-6">
                            Market Value: <span className="text-green-400 font-semibold">$3,280</span> (at $40/tonne)
                        </div>

                        <div className="flex items-center justify-between mb-4">
                            <div className="text-[10px] text-zinc-500">
                                Annual Target: 180 tCO₂ &nbsp; • &nbsp; Progress: 45.6%
                            </div>

                            {/* Circular Gauge */}
                            <div className="relative w-12 h-12 flex items-center justify-center shrink-0">
                                <svg className="w-full h-full transform -rotate-90" viewBox="0 0 36 36">
                                    <circle cx="18" cy="18" r="16" fill="none" className="stroke-zinc-800" strokeWidth="4" />
                                    <circle cx="18" cy="18" r="16" fill="none" className="stroke-green-500" strokeWidth="4" strokeDasharray="100" strokeDashoffset="54" strokeLinecap="round" />
                                </svg>
                                <div className="absolute flex flex-col items-center justify-center">
                                    <span className="text-[9px] font-bold text-green-500">45.6%</span>
                                </div>
                            </div>
                        </div>

                        <div className="flex gap-2">
                            <div className="bg-[#14161b] px-3 py-2 rounded border border-[#2e323a] text-center flex-1">
                                <div className="text-xs text-white font-bold">9</div>
                                <div className="text-[8px] text-zinc-500 uppercase">Credits Today</div>
                            </div>
                            <div className="bg-[#14161b] px-3 py-2 rounded border border-[#2e323a] text-center flex-1">
                                <div className="text-xs text-white font-bold">$360</div>
                                <div className="text-[8px] text-zinc-500 uppercase">Today Value</div>
                            </div>
                        </div>
                    </div>

                    {/* Infrastructure Rows */}
                    <div className="bg-[#1a1d24] border border-[#2e323a] rounded-lg p-5 flex-1 flex flex-col">
                        <div className="flex items-center gap-2 mb-4">
                            <div className="w-1.5 h-3 bg-blue-500 rounded-sm" />
                            <h3 className="font-bold text-xs">Data Center Infrastructure Status</h3>
                        </div>
                        <div className="grid grid-cols-2 gap-3 flex-1 px-1">
                            {[
                                { title: "Server Racks", count: "16/16", sub: "100%", status: "green" },
                                { title: "CRAC Units", count: "4/4", sub: "N+1 OK", status: "green" },
                                { title: "PDUs", count: "32/32", sub: "Dual-feed", status: "green" },
                                { title: "ToR Switches", count: "31/32", sub: "96.8%", status: "orange" },
                                { title: "UPS Systems", count: "2/2", sub: "Backup OK", status: "green" },
                                { title: "Fire Zones", count: "4/4", sub: "FM-200 OK", status: "green" }
                            ].map((infra, idx) => (
                                <div key={idx} className="bg-[#14161b] border border-[#2e323a] p-2 rounded flex flex-col justify-between relative group">
                                    {/* Indicator dot */}
                                    <div className={`absolute top-2 right-2 w-1.5 h-1.5 rounded-full ${infra.status === 'green' ? 'bg-green-500' : 'bg-orange-500'} shadow-[0_0_4px_${infra.status === 'green' ? '#22c55e' : '#f97316'}]`} />
                                    <div className="text-[10px] text-zinc-400 font-bold mb-1 truncate pr-3">{infra.title}</div>
                                    <div className="text-sm font-bold text-white leading-tight">{infra.count}</div>
                                    <div className="text-[8px] text-zinc-500">{infra.sub}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Center Column (Charts) */}
                <div className="flex flex-col gap-5 col-span-2">
                    {/* Real-Time Trends */}
                    <div className="bg-[#1a1d24] border border-[#2e323a] rounded-lg p-6 flex flex-col min-h-[320px] flex-1">
                        <div className="flex items-center gap-2 mb-6">
                            <div className="w-1.5 h-4 bg-[#60a5fa] rounded-sm" />
                            <h3 className="font-bold text-sm">Real-Time Energy Consumption Trends</h3>
                        </div>

                        <div className="flex-1 w-full relative">
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={consumptionData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                                    <defs>
                                        <linearGradient id="colorElec" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#60a5fa" stopOpacity={0.4} />
                                            <stop offset="95%" stopColor="#60a5fa" stopOpacity={0} />
                                        </linearGradient>
                                        <linearGradient id="colorCool" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#a78bfa" stopOpacity={0.4} />
                                            <stop offset="95%" stopColor="#a78bfa" stopOpacity={0} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#2e323a" vertical={false} />
                                    <XAxis dataKey="time" stroke="#71717a" fontSize={11} tickLine={false} axisLine={false} />
                                    <YAxis stroke="#71717a" fontSize={11} tickLine={false} axisLine={false} />
                                    <Tooltip content={<CustomTooltip />} />
                                    <Legend iconType="circle" wrapperStyle={{ fontSize: '11px' }} verticalAlign="top" height={36} />
                                    <Area type="monotone" name="Facility (kW)" dataKey="electrical" stroke="#60a5fa" strokeWidth={2} fillOpacity={1} fill="url(#colorElec)" dot={{ fill: '#1a1d24', strokeWidth: 2, r: 4, stroke: '#60a5fa' }} activeDot={{ r: 6, strokeWidth: 0, fill: '#60a5fa' }} />
                                    <Area type="monotone" name="Cooling (tons)" dataKey="cooling" stroke="#a78bfa" strokeWidth={2} fillOpacity={1} fill="url(#colorCool)" dot={{ fill: '#1a1d24', strokeWidth: 2, r: 4, stroke: '#a78bfa' }} activeDot={{ r: 6, strokeWidth: 0, fill: '#a78bfa' }} />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* Water vs Cooling */}
                    <div className="bg-[#1a1d24] border border-[#2e323a] rounded-lg p-6 flex flex-col min-h-[300px] flex-1">
                        <div className="flex items-center gap-2 mb-4">
                            <div className="w-1.5 h-4 bg-[#2dd4bf] rounded-sm" />
                            <h3 className="font-bold text-sm">Water vs Cooling Efficiency</h3>
                        </div>

                        <div className="flex-1 w-full relative">
                            <ResponsiveContainer width="100%" height="100%">
                                <ComposedChart data={efficiencyData} margin={{ top: 10, right: 0, left: -20, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#2e323a" vertical={false} />
                                    <XAxis dataKey="day" stroke="#71717a" fontSize={11} tickLine={false} axisLine={false} />
                                    <YAxis yAxisId="left" stroke="#71717a" fontSize={11} tickLine={false} axisLine={false} />
                                    <YAxis yAxisId="right" orientation="right" stroke="#71717a" fontSize={11} tickLine={false} axisLine={false} />
                                    <Tooltip content={<CustomTooltip />} />
                                    <Legend iconType="circle" wrapperStyle={{ fontSize: '11px' }} verticalAlign="top" height={36} />
                                    <Bar yAxisId="left" name="Water (Gallons)" dataKey="water" fill="#2dd4bf" radius={[2, 2, 0, 0]} barSize={26} />
                                    <Line yAxisId="right" type="monotone" name="Cooling Efficiency (%)" dataKey="efficiency" stroke="#f87171" strokeWidth={2} dot={{ r: 4, fill: '#f87171', strokeWidth: 0 }} />
                                </ComposedChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>

                {/* Right Column (Donut & Overall Score) */}
                <div className="flex flex-col gap-5 col-span-1">
                    {/* Source Breakdown */}
                    <div className="bg-[#1a1d24] border border-[#2e323a] rounded-lg p-6 flex flex-col items-center min-h-[300px]">
                        <div className="flex items-center gap-2 mb-6 self-start">
                            <div className="w-1.5 h-4 bg-[#60a5fa] rounded-sm" />
                            <h3 className="font-bold text-sm">Energy Source Breakdown</h3>
                        </div>

                        <div className="w-full h-56 relative flex-1">
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={sourceData}
                                        innerRadius={55}
                                        outerRadius={80}
                                        paddingAngle={5}
                                        dataKey="value"
                                        stroke="none"
                                    >
                                        {sourceData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={entry.color} />
                                        ))}
                                    </Pie>
                                    <Tooltip content={<CustomTooltip />} />
                                    <Legend
                                        layout="horizontal"
                                        verticalAlign="bottom"
                                        align="center"
                                        iconType="circle"
                                        wrapperStyle={{ fontSize: '10px', paddingTop: '10px' }}
                                    />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* Overall Efficiency Score */}
                    <div className="bg-[#1a1d24] border border-[#2e323a] rounded-lg p-6 flex flex-col flex-1">
                        <div className="flex items-center gap-2 mb-8">
                            <div className="w-1.5 h-4 bg-[#60a5fa] rounded-sm" />
                            <h3 className="font-bold text-sm">Overall Efficiency Score</h3>
                        </div>

                        <div className="flex flex-col items-center justify-center flex-1 w-full">
                            <div className="relative w-32 h-32 flex items-center justify-center mb-10 shrink-0">
                                <svg className="w-full h-full transform -rotate-90" viewBox="0 0 36 36">
                                    <circle cx="18" cy="18" r="16" fill="none" className="stroke-[#2e323a]" strokeWidth="4" />
                                    <circle cx="18" cy="18" r="16" fill="none" className="stroke-[#2dd4bf] drop-shadow-[0_0_4px_rgba(45,212,191,0.5)]" strokeWidth="4" strokeDasharray="100" strokeDashoffset="8" strokeLinecap="round" />
                                </svg>
                                <div className="absolute flex flex-col items-center justify-center">
                                    <span className="text-3xl font-bold text-[#2dd4bf]">92%</span>
                                    <span className="text-[10px] text-zinc-500 uppercase tracking-widest mt-1">Efficiency</span>
                                </div>
                            </div>

                            <div className="w-full space-y-5">
                                {[
                                    { label: "Energy Efficiency", val: 94, color: "bg-[#2dd4bf]" },
                                    { label: "Cooling Efficiency", val: 91, color: "bg-[#2dd4bf]" },
                                    { label: "Resource Utilization", val: 89, color: "bg-[#60a5fa]" },
                                    { label: "Carbon Footprint", val: 95, color: "bg-[#2dd4bf]" }
                                ].map((item, i) => (
                                    <div key={i} className="flex flex-col gap-1.5">
                                        <div className="flex justify-between text-[10px] font-semibold text-zinc-400">
                                            <span>{item.label}</span>
                                            <span className="text-white">{item.val}%</span>
                                        </div>
                                        <div className="w-full h-1 bg-[#2e323a] rounded-full overflow-hidden">
                                            <div className={`h-full rounded-full ${item.color}`} style={{ width: `${item.val}%` }}></div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default EnergyOverview;

